// getPrice.js

import { fetchDbPriceRules, clearDbPriceRulesCache } from "./templates/prices/dbPriceRules";

let allPrices = {}; // Shared across calls in a single render cycle
export const resetPrices = () => { allPrices = {}; };
export const getPrices = () => { return allPrices; };

// Re-export for convenience
export { fetchDbPriceRules, clearDbPriceRulesCache };

// Clear DB cache (useful when admin updates pricing)
export const clearDbPriceCache = clearDbPriceRulesCache;

// SAFE CONDITION EVALUATOR — replaces eval()
const evalCondition = (condition) => {
  if (!condition) return false;
  try {
    return new Function("doorData", `return (${condition})`)(doorData);
  } catch (e) {
    console.warn("Invalid condition:", condition);
    return false;
  }
};

// UNIVERSAL EXPRESSION READER — works for ALL templates
const getExprValue = (key) => {
  if (!templateMatch?.expressions || typeof templateMatch.expressions !== 'object') {
    return null;
  }
  
  const expr = templateMatch.expressions[key];
  if (!expr) return null;

  try {
    return new Function("doorData", `return ${expr}`)(doorData);
  } catch (e) {
    console.warn(`Expression failed [${key}]: ${expr}`, e);
    return null;
  }
};

// RENDER TEMPLATE — replaces <<key>> with real values
const renderTemplate_not_in_use = (templateString) => {
  if (!templateString || !templateMatch?.expressions) {
    return templateString;
  }

  let result = templateString;

  // Loop through all expressions (name, aaa, bbb, etc.)
  for (const key in templateMatch.expressions) {
    const placeholder = `<<${key}>>`;
    const realValue = getExprValue(key);

    if (realValue !== null && realValue !== undefined) {
      result = result.replaceAll(placeholder, String(realValue));
    }
  }

  return result;
};

export function GetPrice({ templateName = null, dataId = null, doorData = null, templates = [], displayPrices = false, returnOnlyPrice = false, dbPriceRules = null }) {
  let priceRules = [];

  // Use DB price rules if provided, otherwise load from file
  if (dbPriceRules && Array.isArray(dbPriceRules) && dbPriceRules.length > 0) {
    priceRules = dbPriceRules;
  } else if (doorData?.priceRules) {
    // Load pricing rules dynamically from module (sync simulation)
    try {
      const module = require(`./templates/prices/${doorData.priceRules}.js`);

      priceRules = module.default || [];
    } catch (error) {
      alert("Failed to load priceRules module:\n" + `../templates/prices/${doorData.priceRules}.js`)
      console.error("Failed to load priceRules module:", error);
    }
  }

  function evaluatePrice({ formula, unitPrice, table, minValue, multiplicator, other_values, conditionalPrice, surcharge, ...dynamicPrices }, doorData, priceRules) {
    try {
      let result = 1;

      if (table) {
        // 1. Evaluate value and column
        const value = new Function("doorData", `return ${table.valueExpr}`)(doorData);
        const columnName = new Function("doorData", `return ${table.columnExpr}`)(doorData);

        // 2. Loop through conditions (id1, condition1, id2, condition2, etc.)
        const tableIds = Object.keys(table).filter(k => k.startsWith("id"));

        let selectedTableId = null;

        for (const idKey of tableIds) {
          const conditionKey = `condition${idKey.slice(2)}`; // from id1 -> condition1
          const conditionExpr = table[conditionKey];

          if (!conditionExpr) continue;

          const isTrue = new Function("doorData", `return ${conditionExpr}`)(doorData);

          if (isTrue) {
            selectedTableId = table[idKey];
            break; // use the first matching condition
          }
        }

        // 3. Fallback if no condition matches
        if (!selectedTableId) {
          console.warn("No matching condition for table:", table);
          return 0;
        }

        // 4. Find the matching table in priceRules
        const rangeTable = priceRules.find(r => r.id === selectedTableId);
        if (!rangeTable || !Array.isArray(rangeTable.rows)) return 0;

        // 5. Find the price row
        const matchedRow = rangeTable.rows.find(row => value >= row.min && value < row.max);
        let price = matchedRow?.[columnName] ?? 0;

        // 6. Handle expression-based prices (e.g., "10.85*square")
        if (typeof price === 'string' && price.includes('square')) {
          try {
            const expression = price.replace(/square/g, value);
            price = new Function(`return ${expression}`)();
          } catch (e) {
            console.warn("Failed to evaluate price expression:", price, e);
            price = 0;
          }
        }

        // 7. Handle surcharge (optional add-on)
        if (surcharge && surcharge.condition) {
          try {
            const shouldAddSurcharge = new Function("doorData", "result", `return (${surcharge.condition})`)(doorData, price);
            console.log('[DEBUG Surcharge] condition:', surcharge.condition, 'price:', price, 'shouldAdd:', shouldAddSurcharge);
            if (shouldAddSurcharge) {
              let surchargeValue = 0;
              if (typeof surcharge.add === 'number') {
                // Fixed value
                surchargeValue = surcharge.add;
              } else if (Array.isArray(surcharge.add)) {
                // 1D lookup: [lookupId, keyExpr]
                const [lookupId, keyExpr] = surcharge.add;
                const lookupRule = priceRules.find(r => r.id === lookupId);
                const lookupKey = new Function("doorData", `return ${keyExpr}`)(doorData);
                surchargeValue = lookupRule?.[lookupKey] ?? 0;
              } else if (typeof surcharge.add === 'object') {
                // 2D lookup: { id, key1, key2 }
                const lookupRule = priceRules.find(r => r.id === surcharge.add.id);
                const k1 = new Function("doorData", `return ${surcharge.add.key1}`)(doorData);
                const k2 = new Function("doorData", `return ${surcharge.add.key2}`)(doorData);
                console.log('[DEBUG Surcharge 2D] lookupId:', surcharge.add.id, 'k1:', k1, 'k2:', k2);
                console.log('[DEBUG Surcharge 2D] lookupRule found?:', !!lookupRule);
                console.log('[DEBUG Surcharge 2D] lookupRule[k1]:', lookupRule?.[k1]);
                console.log('[DEBUG Surcharge 2D] surchargeValue:', lookupRule?.[k1]?.[k2]);
                surchargeValue = lookupRule?.[k1]?.[k2] ?? 0;
              }
              console.log('[DEBUG Surcharge] final surchargeValue:', surchargeValue);
              price += surchargeValue;
            }
          } catch (e) {
            console.warn("Failed to evaluate surcharge:", surcharge, e);
          }
        }

        return price;
      }

      if (0) {
        const rangeTable = priceRules.find(r => r.id === table.id);
        if (!rangeTable || !Array.isArray(rangeTable.rows)) return 0;
      
        const value = new Function("doorData", `return ${table.valueExpr}`)(doorData);
        const columnName = new Function("doorData", `return ${table.columnExpr}`)(doorData);
        const matchedRow = rangeTable.rows.find(row =>
          value >= row.min && value < row.max
        );
      
        const price = matchedRow?.[columnName] ?? 0;
        return price; // ✅ EXIT HERE so it’s not multiplied again
      }
      
      if (0) {
        const rangeTable = priceRules.find(r => r.id === table.id);
        if (!rangeTable || !Array.isArray(rangeTable.rows)) return 0;
      
        const value = new Function("doorData", `return ${table.valueExpr}`)(doorData);
        const columnName = new Function("doorData", `return ${table.columnExpr}`)(doorData);
      
        const matchedRow = rangeTable.rows.find(row =>
          value >= row.min && value < row.max
        );
      
        const price = matchedRow?.[columnName] ?? -22;
        return price;
      }
      

      if (formula) {
        let finalFormula = formula;

        for (const key in dynamicPrices) {
          if (key.startsWith("price")) {
            const priceConfig = dynamicPrices[key];
            let priceValue = 0;

            // Check if it's an object (2D lookup) or array (1D lookup)
            if (Array.isArray(priceConfig)) {
              // Original 1D lookup: ["ref_GlassEnergy", "doorData.glass.glassEnergy1"]
              const [lookupId, valueExpr] = priceConfig;
              const lookupRule = priceRules.find(r => r.id === lookupId);
              const lookupKey = new Function("doorData", `return ${valueExpr}`)(doorData);
              priceValue = lookupRule?.[lookupKey] ?? 0;
            } else if (typeof priceConfig === 'object') {
              // 2D lookup: { id: "ref_GlassEnergy", key1: "doorData.glass.glassThickness1", key2: "doorData.glass.glassEnergy1" }
              const lookupRule = priceRules.find(r => r.id === priceConfig.id);
              const k1 = new Function("doorData", `return ${priceConfig.key1}`)(doorData);
              const k2 = new Function("doorData", `return ${priceConfig.key2}`)(doorData);
              priceValue = lookupRule?.[k1]?.[k2] ?? 0;
            }

            finalFormula = finalFormula.replaceAll(`<<${key}>>`, priceValue);
          }
        }

        result = new Function("doorData", `return ${finalFormula}`)(doorData);

        // Handle multiplicator - supports both single (condition/value) and multiple (condition1/value1, condition2/value2, etc.)
        if (multiplicator) {
          // Check for legacy single condition/value format
          if (multiplicator.condition && multiplicator.value) {
            const shouldMultiply = new Function("doorData", `return (${multiplicator.condition})`)(doorData);
            if (shouldMultiply) result *= multiplicator.value;
          }

          // Check for numbered conditions (condition1/value1, condition2/value2, etc.)
          const conditionKeys = Object.keys(multiplicator).filter(k => k.match(/^condition\d+$/)).sort();
          for (const condKey of conditionKeys) {
            const num = condKey.replace('condition', '');
            const valueKey = `value${num}`;

            if (multiplicator[condKey] && multiplicator[valueKey] !== undefined) {
              const shouldMultiply = new Function("doorData", `return (${multiplicator[condKey]})`)(doorData);
              if (shouldMultiply) {
                result *= multiplicator[valueKey];
              }
            }
          }
        }

        // Handle other_values - like multiplicator but REPLACES result instead of multiplying
        // Uses first matching condition (stops after first match)
        if (other_values) {
          const conditionKeys = Object.keys(other_values).filter(k => k.match(/^condition\d+$/)).sort();
          for (const condKey of conditionKeys) {
            const num = condKey.replace('condition', '');
            const valueKey = `value${num}`;

            if (other_values[condKey] && other_values[valueKey] !== undefined) {
              const shouldReplace = new Function("doorData", `return (${other_values[condKey]})`)(doorData);
              if (shouldReplace) {
                result = other_values[valueKey];
                break; // Stop after first match - replace only once
              }
            }
          }
        }

        if (minValue !== null && Number(result) < Number(minValue)) {
          result = minValue;
        }
 
        //========= new code starts ===========
        //if (eval(conditionalPrice?.condition1)) { //condition 1
        if (conditionalPrice?.condition1 && evalCondition(conditionalPrice.condition1)) {
          if (conditionalPrice?.unitPrice1 > 0){
            unitPrice = conditionalPrice?.unitPrice1; //make new price/unit and continue
          }
          else {
            return conditionalPrice?.value1; //just return entire total price
          }
        }
        //if (eval(conditionalPrice?.condition2)) { //condition 2
        if (conditionalPrice?.condition2 && evalCondition(conditionalPrice.condition2)) {
          if (conditionalPrice?.unitPrice2 > 0){
            unitPrice = conditionalPrice?.unitPrice2;
          }
          else {
            return conditionalPrice?.value2;
          }
        }
        //if (eval(conditionalPrice?.condition3)) { //condition 3
        if (conditionalPrice?.condition3 && evalCondition(conditionalPrice.condition3)) {
          if (conditionalPrice?.unitPrice3 > 0){
            unitPrice = conditionalPrice?.unitPrice3;
          }
          else {
            return conditionalPrice?.value3;
          }
        }
        //if (eval(conditionalPrice?.condition4)) { //condition 4
        if (conditionalPrice?.condition4 && evalCondition(conditionalPrice.condition4)) {
          if (conditionalPrice?.unitPrice4 > 0){
            unitPrice = conditionalPrice?.unitPrice4;
          }
          else {
            return conditionalPrice?.value4;
          }
        }
        //if (eval(conditionalPrice?.condition5)) { //condition 5
        if (conditionalPrice?.condition5 && evalCondition(conditionalPrice.condition5)) {
          if (conditionalPrice?.unitPrice5 > 0){
            unitPrice = conditionalPrice?.unitPrice5;
          }
          else {
            return conditionalPrice?.value5;
          }
        }
        //======= new code ends =====
        return result * unitPrice;
      }

      return unitPrice;
    } catch (e) {
      console.error("Error in pricing formula:", e);
      return 0;
    }
  }

  if (dataId === "TOTALS") {
    const quantity = Number(doorData?.frameData?.quantity || 1);
    const baseSubtotal = Object.values(allPrices).reduce((sum, p) => sum + p, 0);
    const subtotal = baseSubtotal * quantity;
    const tax = subtotal * 0.13;
    const total = subtotal + tax;

    doorData.calc.subtotal = subtotal.toFixed(2);
    doorData.calc.tax = tax.toFixed(2);
    doorData.calc.total = total.toFixed(2);

    return returnOnlyPrice ? { subtotal, tax, total } : null;
  }

  const priceRule = priceRules.find(rule => rule.id === dataId);
  const templateMatch = templates.find(t => t.id === dataId);

  // Debug for top2_grillType
  if (dataId === 'top2_grillType') {
    console.log('[DEBUG GetPrice] top2_grillType priceRule:', priceRule);
    console.log('[DEBUG GetPrice] top2_grillType priceRules count:', priceRules.length);
    console.log('[DEBUG GetPrice] dbPriceRules passed?:', !!dbPriceRules);
  }

  const isTemplateVisible = !templateMatch || !templateMatch.condition
    ? true
    : new Function("doorData", `return (${templateMatch.condition})`)(doorData);

  // Check if price rule's own condition allows calculation (if condition exists and is false, skip)
  const isPriceRuleEnabled = !priceRule || !priceRule.condition
    ? true
    : new Function("doorData", `return (${priceRule.condition})`)(doorData);

  // Debug for top2_grillType
  if (dataId === 'top2_grillType') {
    console.log('[DEBUG GetPrice] isPriceRuleEnabled:', isPriceRuleEnabled);
    console.log('[DEBUG GetPrice] priceRule.condition:', priceRule?.condition);
  }

  const shouldCalculatePrice = isTemplateVisible && isPriceRuleEnabled && !!priceRule;

  // Debug for top2_grillType
  if (dataId === 'top2_grillType') {
    console.log('[DEBUG GetPrice] shouldCalculatePrice:', shouldCalculatePrice);
    console.log('[DEBUG GetPrice] isTemplateVisible:', isTemplateVisible);
  }

  const price = shouldCalculatePrice
    ? evaluatePrice(priceRule, doorData, priceRules)
    : null;

  // Debug for top2_grillType
  if (dataId === 'top2_grillType') {
    console.log('[DEBUG GetPrice] calculated price:', price);
  }

  if (shouldCalculatePrice && !isNaN(price)) {
    allPrices[dataId] = price;
  }

  if (returnOnlyPrice) {
    const priceSource = priceRule?.formula ? 'formulas' : 'rules';
    return { price, isVisible: isTemplateVisible, priceRuleId: dataId ?? null, priceSource };
  }

  if (price === undefined || price === null || isNaN(price)) {
    console.warn(`⚠️ Warning: No valid price for "${dataId}". Price:`, price);
  }

  return displayPrices && shouldCalculatePrice ? `$${price.toFixed(2)}` : '';
}
